import React, { useEffect, useCallback, useState } from 'react';
import { useParams, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { ArrowLeft, Menu, X, Loader2 } from 'lucide-react';
import { WorkflowProgressBar } from './shared/WorkflowProgressBar';
import { ContractSidebar } from './shared/ContractSidebar';
import { useContractWorkflow } from './hooks/useContractWorkflow';
import { WorkflowStep, WorkflowContext } from '../../../types/workflow.types';
import { GlobalNavigation } from '../../common/GlobalNavigation';
import { decodeHtmlEntities } from '../../../utils/textUtils';

// Default workflow steps
const defaultSteps: Omit<WorkflowStep, 'status'>[] = [
  { id: 1, name: 'Contract Overview', shortName: 'Overview', icon: null, path: 'overview' },
  { id: 2, name: 'AI Analysis', shortName: 'Analysis', icon: null, path: 'analysis' },
  { id: 3, name: 'Clause Review', shortName: 'Review', icon: null, path: 'clauses' },
  { id: 4, name: 'Summary', shortName: 'Summary', icon: null, path: 'summary' },
  { id: 5, name: 'Negotiation', shortName: 'Negotiate', icon: null, path: 'negotiation' },
];

// Loading skeleton component
const WorkflowLoadingSkeleton: React.FC = () => (
  <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
    <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border py-6 px-4">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        {[1, 2, 3, 4, 5].map((i) => (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse" />
              <div className="w-16 h-4 mt-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
            {i < 5 && <div className="flex-1 h-1 mx-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />}
          </React.Fragment>
        ))}
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex gap-6">
        <div className="w-80 flex-shrink-0">
          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6 space-y-4 animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
          </div>
        </div>
        <div className="flex-1">
          <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-6 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Error component
const WorkflowError: React.FC<{ error: string; onRetry: () => void; onBack: () => void }> = ({
  error,
  onRetry,
  onBack
}) => (
  <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-4">
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-8 max-w-md text-center">
      <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
        <X className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Failed to Load Contract
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={onRetry}
          className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  </div>
);

export const ContractWorkflowPage: React.FC = () => {
  const { id: contractId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const workflowData = useContractWorkflow(contractId);
  const {
    contract,
    analysis,
    clauseDecisions,
    selectedForNegotiation,
    workflowState,
    isLoading,
    isSaving,
    error,
    currentStep,
    setCurrentStep,
    saveProgress,
    canProceedToStep,
    updateClauseDecision,
    toggleNegotiationSelection,
    refreshContract,
    refreshAnalysis,
  } = workflowData;

  // Determine current step from URL
  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const currentPath = pathParts[pathParts.length - 1];
    const stepIndex = defaultSteps.findIndex(s => s.path === currentPath);
    if (stepIndex >= 0) {
      setCurrentStep(stepIndex + 1);
    }
  }, [location.pathname, setCurrentStep]);

  // Handle step navigation
  const handleStepClick = useCallback((step: WorkflowStep) => {
    if (canProceedToStep(step.id)) {
      navigate(`/contracts/${contractId}/${step.path}`);
      setMobileSidebarOpen(false);
    }
  }, [contractId, navigate, canProceedToStep]);

  // Navigate to next step
  const handleNextStep = useCallback(() => {
    console.log('handleNextStep called');
    console.log('currentStep:', currentStep);
    console.log('canProceedToStep(currentStep + 1):', canProceedToStep(currentStep + 1));
    console.log('contractId:', contractId);

    if (currentStep < 5 && canProceedToStep(currentStep + 1)) {
      const nextStep = defaultSteps[currentStep];
      console.log('Navigating to:', `/contracts/${contractId}/${nextStep.path}`);
      navigate(`/contracts/${contractId}/${nextStep.path}`);
    } else {
      console.log('Navigation blocked - conditions not met');
    }
  }, [currentStep, canProceedToStep, contractId, navigate]);

  // Navigate to previous step
  const handlePrevStep = useCallback(() => {
    if (currentStep > 1) {
      const prevStep = defaultSteps[currentStep - 2];
      navigate(`/contracts/${contractId}/${prevStep.path}`);
    }
  }, [currentStep, contractId, navigate]);

  // Go back to contracts list
  const handleBackToList = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Prepare workflow context for child routes
  const workflowContext: WorkflowContext = {
    contract,
    analysis,
    clauseDecisions,
    selectedForNegotiation,
    workflowState,
    isLoading,
    isSaving,
    error,
    onSaveProgress: saveProgress,
    onNavigateToStep: handleStepClick,
    updateClauseDecision,
    toggleNegotiationSelection,
  };

  // Generate breadcrumbs with contract name instead of ID
  const getBreadcrumbs = () => {
    const contractName = contract ? decodeHtmlEntities(contract.contractTitle) : 'Loading...';
    const activeStep = defaultSteps.find(s => location.pathname.includes(s.path));
    return [
      { label: 'Home', path: '/' },
      { label: 'Contracts', path: '/contracts' },
      { label: contractName, path: `/contracts/${contractId}/workflow/overview` },
      ...(activeStep ? [{ label: activeStep.name }] : [])
    ];
  };

  if (isLoading) {
    return (
      <>
        <GlobalNavigation
          title="Contract Workflow"
          showBackButton={false}
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Contracts', path: '/contracts' },
            { label: 'Loading...' }
          ]}
        />
        <WorkflowLoadingSkeleton />
      </>
    );
  }

  if (error && !contract) {
    return (
      <>
        <GlobalNavigation
          title="Contract Workflow"
          showBackButton={false}
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Contracts', path: '/contracts' },
            { label: 'Error' }
          ]}
        />
        <WorkflowError
          error={error}
          onRetry={() => window.location.reload()}
          onBack={handleBackToList}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg">
      {/* Global Navigation */}
      <GlobalNavigation
        title={decodeHtmlEntities(contract?.contractTitle) || 'Contract Workflow'}
        showBackButton={false}
        breadcrumbs={getBreadcrumbs()}
      />

      {/* Back button and title bar */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBackToList}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Contracts</span>
              </button>
              {contract && (
                <div className="hidden md:block">
                  <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate max-w-md">
                    {decodeHtmlEntities(contract.contractTitle)}
                  </h1>
                </div>
              )}
            </div>

            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>

            {/* Saving indicator */}
            {isSaving && (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Saving...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Progress Bar */}
      <div className="bg-white dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <WorkflowProgressBar
            currentStep={currentStep}
            onStepClick={handleStepClick}
            allowNavigation={true}
          />
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar - Hidden on mobile, shown on large screens */}
          <div className={`
            hidden lg:block w-80 flex-shrink-0 transition-all duration-300
            ${sidebarCollapsed ? 'w-12' : 'w-80'}
          `}>
            <ContractSidebar
              contract={contract}
              analysis={analysis}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
            />
          </div>

          {/* Mobile Sidebar Overlay */}
          {mobileSidebarOpen && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setMobileSidebarOpen(false)}
              />
              <div className="absolute left-0 top-0 bottom-0 w-80 bg-white dark:bg-dark-bg overflow-y-auto">
                <div className="p-4">
                  <button
                    onClick={() => setMobileSidebarOpen(false)}
                    className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <ContractSidebar contract={contract} analysis={analysis} />
                </div>
              </div>
            </div>
          )}

          {/* Main Content - Step Components */}
          <div className="flex-1 min-w-0">
            <Outlet context={{
              ...workflowContext,
              onNextStep: handleNextStep,
              onPrevStep: handlePrevStep,
              refreshContract,
              refreshAnalysis,
              getStepProgress: workflowData.getStepProgress,
              runAnalysis: workflowData.runAnalysis,
              setSelectedForNegotiation: workflowData.setSelectedForNegotiation,
              bulkUpdateClauseDecisions: workflowData.bulkUpdateClauseDecisions,
              completeReview: workflowData.completeReview,
            }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to use workflow context in child components
export function useWorkflowOutletContext() {
  const context = React.useContext(React.createContext<WorkflowContext | null>(null));
  // Note: This is a placeholder. In actual usage, use useOutletContext from react-router-dom
  return context;
}
